import React, { useState, useRef } from "react"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import MediaContentDisplay from "components/common/MediaManager/MediaContentDisplay"
import FormControl from "@mui/material/FormControl"
import { SketchPicker } from "react-color"
import RichTextEditor from "components/common/RichTextEditor"
import { verifyResponse } from "utils/httpRequest"
import { isDarkColorByHex, isDarkColorByRGB } from "utils/color"
import { editSystemSetting } from "services/systemSetting"
import { MEDIA_TYPE_INDEX } from "constants/global"
import { useForm } from "react-hook-form"
import { useSnackbar } from "notistack"
import { SYSTEM_SETTINGS_INDEX } from "constants/global"
import {
  StyledSystemSettingDialog,
  StyledInputLabel,
} from "./SystemSetting.style"

const SystemSettingDialog = (props) => {
  const { enqueueSnackbar } = useSnackbar()
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      resourceId: null,
    },
  })

  const { onSubmit, openMediaManager } = props
  const [systemSetting, setSystemSetting] = useState({})
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isDarkColor, setIsDarkColor] = useState(false)
  const [resource, setResource] = useState({})
  const resourceRef = useRef(null)

  const getContentLabel = (type) => {
    if (type === SYSTEM_SETTINGS_INDEX.TEXT) return "Text"
    if (type === SYSTEM_SETTINGS_INDEX.NUMBER) return "Number Value"
    if (type === SYSTEM_SETTINGS_INDEX.LINK) return "HTML Link"
    if (type === SYSTEM_SETTINGS_INDEX.COLOR) return "Color Code"
    return "Content"
  }

  const handleClose = () => {
    setOpen(false)
  }

  const openDlg = (editSystemSetting) => {
    const setting = editSystemSetting || {}
    reset()
    setSystemSetting(setting)
    if (editSystemSetting) {
      setValue("resourceId", setting.resourceId || null)
      resourceRef.current = setting.resourceDto || null
      setValue("content", setting.content || "")
      updateResource()
      setValue("htmlContent", setting.htmlContent || "")
      setValue("description", setting.description || "")
      if (setting.type === SYSTEM_SETTINGS_INDEX.COLOR && setting.content) {
        try {
          setIsDarkColor(isDarkColorByHex(setting.content))
        } catch {
          setIsDarkColor(false)
        }
      } else {
        setIsDarkColor(false)
      }
    } else {
      setValue("resourceId", null)
      setValue("content", "")
      setValue("htmlContent", "")
      setValue("description", "")
      resourceRef.current = null
      setIsDarkColor(false)
      setResource({})
    }
    setOpen(true)
  }

  const handleAction = async (data) => {
    await editSetting(data)
  }

  const clearResource = () => {
    setValue("resourceId", null)
    resourceRef.current = null
    updateResource()
  }

  const updateResource = () => {
    setResource({
      ...(resourceRef.current || {}),
      selectAction: selectIntroResource,
      clearResourceAction: clearResource,
    })
  }

  const getResourceSelectType = () => {
    if (systemSetting.type === SYSTEM_SETTINGS_INDEX.VIDEO)
      return [MEDIA_TYPE_INDEX.video]

    if (systemSetting.type === SYSTEM_SETTINGS_INDEX.AUDIO)
      return [MEDIA_TYPE_INDEX.audio]

    if (systemSetting.type === SYSTEM_SETTINGS_INDEX.IMAGE)
      return [MEDIA_TYPE_INDEX.image]

    if (systemSetting.type === SYSTEM_SETTINGS_INDEX.ATTACHMENT)
      return [MEDIA_TYPE_INDEX.document]

    if (systemSetting.type === SYSTEM_SETTINGS_INDEX.MEDIA)
      return [
        MEDIA_TYPE_INDEX.image,
        MEDIA_TYPE_INDEX.video,
        MEDIA_TYPE_INDEX.audio,
      ]

    return [
      MEDIA_TYPE_INDEX.video,
      MEDIA_TYPE_INDEX.audio,
      MEDIA_TYPE_INDEX.image,
      MEDIA_TYPE_INDEX.document,
    ]
  }

  const selectIntroResource = () => {
    openMediaManager((media) => {
      resourceRef.current = media
      setValue("resourceId", media.id)
      updateResource()
    }, getResourceSelectType())
  }

  const editSetting = async (data) => {
    const postData = {
      ...data,
      id: systemSetting.id,
    }
    try {
      setLoading(true)
      const response = await editSystemSetting(postData)
      verifyResponse(response)
      if (response.data.rtnCode === 200) {
        enqueueSnackbar("System Setting updated", {
          variant: "success",
        })
      } else {
        enqueueSnackbar("System Setting update Fail", { variant: "warning" })
      }
    } catch {
      enqueueSnackbar("System Setting update failed", { variant: "warning" })
    } finally {
      setLoading(false)
      onSubmit()
      setOpen(false)
    }
  }

  const changeColorValue = (e) => {
    setValue("content", e.hex)
    setIsDarkColor(isDarkColorByRGB(e.rgb))
  }

  const dlg = (
    <StyledSystemSettingDialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">Edit System Setting</h2>
            <p className="dialog-subtitle">
              Update setting metadata, content value, and media/resource mapping.
            </p>
          </div>
        </DialogTitle>
        <DialogContent>
          <FormControl>
            <TextField
              autoFocus
              autoComplete="off"
              id="system-setting-title"
              label="Title"
              type="text"
              fullWidth
              defaultValue={systemSetting.title}
              {...register("title")}
              disabled
              variant="outlined"
              size="small"
            />
          </FormControl>
          <FormControl>
            <TextField
              autoComplete="off"
              id="system-setting-tag"
              label="Tag"
              type="text"
              fullWidth
              defaultValue={systemSetting.tag}
              {...register("tag")}
              disabled
              variant="outlined"
              size="small"
            />
          </FormControl>

          <FormControl>
            <RichTextEditor
              value={watch("description") || systemSetting.description || ''}
              onChange={(value) => {
                setValue("description", value)
                setSystemSetting({ ...systemSetting, description: value })
              }}
              label="Description"
              placeholder="Enter system setting description..."
              toolbarConfig="description"
              minHeight="120px"
              error={!!errors?.description}
              helperText={errors?.description ? "Description is required" : " "}
            />
          </FormControl>

          {(systemSetting.type === SYSTEM_SETTINGS_INDEX.TEXT ||
            systemSetting.type === SYSTEM_SETTINGS_INDEX.NUMBER ||
            systemSetting.type === SYSTEM_SETTINGS_INDEX.LINK ||
            systemSetting.type === SYSTEM_SETTINGS_INDEX.COLOR) && (
            <FormControl>
              <TextField
                label={getContentLabel(systemSetting.type)}
                id="system-setting-content"
                fullWidth
                variant="outlined"
                size="small"
                type={
                  systemSetting.type === SYSTEM_SETTINGS_INDEX.NUMBER
                    ? "number"
                    : "text"
                }
                defaultValue={systemSetting.content}
                {...register("content", { required: true })}
                error={!!errors?.content}
                helperText={errors?.content ? "Content is required" : " "}
              />
            </FormControl>
          )}

          {systemSetting.type === SYSTEM_SETTINGS_INDEX.HTML && (
            <FormControl>
              <StyledInputLabel className="dialog-label">HTML Content</StyledInputLabel>
              <RichTextEditor
                value={watch("htmlContent") || systemSetting.htmlContent || ''}
                onChange={(value) => {
                  setValue("htmlContent", value)
                  setSystemSetting({ ...systemSetting, htmlContent: value })
                }}
                placeholder="Enter HTML content..."
                toolbarConfig="full"
                minHeight="200px"
              />
            </FormControl>
          )}

          {(systemSetting.type === SYSTEM_SETTINGS_INDEX.IMAGE ||
            systemSetting.type === SYSTEM_SETTINGS_INDEX.AUDIO ||
            systemSetting.type === SYSTEM_SETTINGS_INDEX.VIDEO ||
            systemSetting.type === SYSTEM_SETTINGS_INDEX.MEDIA ||
            systemSetting.type === SYSTEM_SETTINGS_INDEX.ATTACHMENT) && (
            <div className="resource-section">
              <StyledInputLabel>Resource</StyledInputLabel>
              <input
                type="hidden"
                defaultValue={systemSetting.resourceId}
                {...register("resourceId")}
              />
              <MediaContentDisplay resource={resource} />
            </div>
          )}

          {systemSetting.type === SYSTEM_SETTINGS_INDEX.COLOR && (
            <FormControl>
              <StyledInputLabel>Pick Color</StyledInputLabel>
              <div
                className={`color-box ${isDarkColor ? "dark-mode" : ""}`}
                style={{
                  backgroundColor: watch("content"),
                }}
              >
                {watch("content")}
              </div>
              <div>
                <SketchPicker
                  color={watch("content")}
                  onChangeComplete={changeColorValue}
                />
              </div>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-cancel-btn"
            variant="outlined"
            size="medium"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className="dialog-submit-btn"
            type="submit"
            variant="contained"
            size="medium"
          >
            Update
          </Button>
        </DialogActions>
      </form>
    </StyledSystemSettingDialog>
  )

  return [openDlg, dlg, loading]
}

export default SystemSettingDialog
